/* US Boat Cover Finder — picks the line illustration for one finder row.

   The finder's third step ("Select your exact boat") shows one tile per config
   row. This module turns a row into the filename of one of the 51 `os_*.png`
   illustrations in assets/, so the tile can show the boat the shopper is about
   to pick rather than a wall of text.

   Three inputs decide it:
     hull    — from boatType  (bass / deep-v / mod-v / aluminum / generic)
     layout  — from the model name first, then boatType, then the cover code
               family (MA 202 = open/tinny, MA 204 = centre console, MA 205 =
               side console, MA 206 = jon, MA 208/209 = bass, MA 200 = bowrider,
               MA 505 = T-top). The sheets don't carry a layout column, so the
               model name ("… SC O/B", "… TILLER O/B", "… RETRIEVER CC") is the
               most reliable signal we have.
     features— parsed out of configLabel, respecting WITH vs WITHOUT.

   Then it walks a preference list of filenames from most specific to least and
   returns the first one that actually ships. Nothing is invented: FILES is the
   whole set, and a row with no match returns null (the tile just renders text).

   Conventions worth knowing:
   - "WITH HARD OR SOFT T-TOP" draws the hard top — there is no combined art.
   - "WITH OR WITHOUT X" is treated as WITH, so the tile shows the accessory the
     cover has to accommodate.
   - Every illustration is drawn to the same scale on a 512x217 canvas, so tiles
     line up without per-image tuning.

   Shared by the storefront (assets/us-boat-cover-finder.js) and the audit
   script (scripts/audit-us-finder-illustrations.mjs) so there is one source of
   truth for the mapping. Review CSV: docs/us-finder-illustrations.csv.
*/
(function (root, factory) {
  var api = factory();
  if (typeof module === "object" && module.exports) { module.exports = api; }
  if (root) { root.OSUSBoatIllustration = api; }
})(typeof globalThis !== "undefined" ? globalThis : this, function () {
  "use strict";

  /* Every illustration that ships in assets/. Keep in step with the files. */
  var FILES = [
    "os_aluminum-side-console",
    "os_aluminum-tiller",
    "os_aluminum-tiller_trolling-motor",
    "os_bass-dual-console",
    "os_bass-dual-console_trolling-motor",
    "os_bass-dual-console_trolling-motor_jack-plate",
    "os_bass-side-console",
    "os_bass-side-console_jack-plate",
    "os_bass-side-console_trolling-motor",
    "os_bass-side-console_trolling-motor_jack-plate",
    "os_bowrider_single-engine",
    "os_bowrider_single-engine_bimini-folded_swim-platform",
    "os_bowrider_single-engine_swim-platform",
    "os_bowrider_sterndrive",
    "os_bowrider_sterndrive_bimini-folded_swim-platform",
    "os_bowrider_sterndrive_swim-platform",
    "os_center-console_hard-ttop_single-engine",
    "os_center-console_hard-ttop_single-engine_full-windshield",
    "os_center-console_hard-ttop_single-engine_rails",
    "os_center-console_hard-ttop_single-engine_ski-tow",
    "os_center-console_hard-ttop_twin-engine",
    "os_center-console_hard-ttop_twin-engine_full-windshield",
    "os_center-console_no-ttop_single-engine",
    "os_center-console_single-engine",
    "os_center-console_single-engine_rails",
    "os_center-console_single-engine_ski-tow",
    "os_center-console_soft-ttop_single-engine",
    "os_center-console_soft-ttop_single-engine_rails",
    "os_center-console_soft-ttop_single-engine_ski-tow",
    "os_deep-v-dual-console",
    "os_deep-v-side-console",
    "os_deep-v-side-console_trolling-motor",
    "os_deep-v-tiller",
    "os_deep-v-tiller_trolling-motor",
    "os_dual-console_single-engine",
    "os_jon-open",
    "os_jon-tiller",
    "os_mod-v-side-console",
    "os_mod-v-tiller",
    "os_mod-v-tiller_trolling-motor",
    "os_pilothouse_hard-ttop_twin-engine",
    "os_side-console",
    "os_side-console_bimini-folded",
    "os_side-console_ski-tow",
    "os_side-console_sports-arch",
    "os_side-console_trolling-motor",
    "os_side-console_trolling-motor_jack-plate",
    "os_tiller",
    "os_tiller_jack-plate",
    "os_walkaround_hard-ttop_twin-engine",
    "os_walkaround_single-engine"
  ];

  var HAS = {};
  for (var i = 0; i < FILES.length; i++) { HAS[FILES[i]] = true; }

  function norm(s) {
    return String(s == null ? "" : s)
      .toUpperCase()
      .replace(/[‘’“”]/g, '"')
      .replace(/\s+/g, " ")
      .trim();
  }

  /* Splits a config label into [polarity, phrase] clauses so "WITH HARD T-TOP
     WITHOUT SKI TOW" doesn't read as having a ski tow. "WITH OR WITHOUT X"
     collapses to WITH first, so the accessory is drawn. */
  function clauses(label) {
    var text = norm(label).replace(/\bWITH OR WITHOUT\b/g, "WITH");
    var out = [];
    var re = /\b(WITHOUT|WITH)\b/g;
    var m, last = null, start = 0;
    while ((m = re.exec(text)) !== null) {
      if (last === null) {
        // Anything before the first WITH is a positive clause of its own —
        // "STERNDRIVE WITH SWIM PLATFORM" is a sterndrive.
        if (m.index > 0) { out.push([true, text.slice(0, m.index).trim()]); }
      } else {
        out.push([last, text.slice(start, m.index).trim()]);
      }
      last = m[1] === "WITHOUT" ? false : true;
      start = re.lastIndex;
    }
    if (last !== null) { out.push([last, text.slice(start).trim()]); }
    // A label with no WITH/WITHOUT at all ("STANDARD", "STERNDRIVE") is still
    // information — carry it as a positive clause.
    if (!out.length && text) { out.push([true, text]); }
    return out;
  }

  /* True when `re` appears in a positive clause; false when it only appears in
     a negated one; null when it isn't mentioned. */
  function feature(cls, re) {
    var seen = null;
    for (var i = 0; i < cls.length; i++) {
      if (re.test(cls[i][1])) {
        if (cls[i][0]) { return true; }
        seen = false;
      }
    }
    return seen;
  }

  function hullOf(boatType) {
    var t = norm(boatType);
    if (t.indexOf("BASS") === 0 || t.indexOf("BASS BOAT") >= 0) { return "bass"; }
    if (t.indexOf("DEEP-V") >= 0 || t.indexOf("DEEP V") >= 0) { return "deep-v"; }
    if (t.indexOf("MOD-V") >= 0 || t.indexOf("MOD V") >= 0) { return "mod-v"; }
    if (t.indexOf("ALUMINUM") >= 0 || t.indexOf("ALUMINIUM") >= 0) { return "aluminum"; }
    return "generic";
  }

  /* Layout from the model name — the sheets spell it out in the model string
     ("1450 DISCOVERY SC O/B", "1650 PRO TILLER O/B", "1860 RETRIEVER CC"). */
  function layoutFromModel(model, baseModel) {
    var t = norm(model + " " + baseModel);
    // JON before TILLER: a "1040 JON TILLER" is a jon boat with a tiller, and
    // the jon branch below picks the tiller art itself.
    if (/\bJON\b/.test(t)) { return "jon"; }
    if (/\bTILLER\b/.test(t)) { return "tiller"; }
    if (/\b(CENTER CONSOLE|CENTRE CONSOLE|CC|FCC|RCC)\b/.test(t)) { return "center-console"; }
    if (/\b(DUAL CONSOLE|DC)\b/.test(t)) { return "dual-console"; }
    if (/\b(SIDE CONSOLE|SC)\b/.test(t)) { return "side-console"; }
    if (/\bBOWRIDER\b/.test(t)) { return "bowrider"; }
    if (/\bWALKAROUND\b/.test(t)) { return "walkaround"; }
    if (/\bPILOTHOUSE\b/.test(t)) { return "pilothouse"; }
    return null;
  }

  function layoutFromType(boatType) {
    var t = norm(boatType);
    if (t.indexOf("CENTER CONSOLE") >= 0 || t.indexOf("CENTRE CONSOLE") >= 0) { return "center-console"; }
    if (t.indexOf("DUAL CONSOLE") >= 0) { return "dual-console"; }
    if (t.indexOf("SIDE CONSOLE") >= 0) { return "side-console"; }
    if (t.indexOf("TILLER") >= 0) { return "tiller"; }
    if (t.indexOf("JON") >= 0) { return "jon"; }
    if (t.indexOf("BOWRIDER") >= 0) { return "bowrider"; }
    if (t.indexOf("WALKAROUND") >= 0) { return "walkaround"; }
    if (t.indexOf("PILOTHOUSE") >= 0) { return "pilothouse"; }
    return null;   // "Aluminum Fishing Boat", "Mod-V Fishing Boat", "Bass Boat"
  }

  /* The cover code is picked by cover shape, so its family is a good last
     resort for the three boat types that don't name a layout. */
  function layoutFromCovers(covers) {
    for (var i = 0; i < (covers || []).length; i++) {
      var fam = norm(covers[i].code).replace(/\s+/g, " ").split("-")[0].trim();
      if (fam === "MA 202") { return "tiller"; }
      if (fam === "MA 204" || fam === "MA 505") { return "center-console"; }
      if (fam === "MA 205" || fam === "MA 208" || fam === "MA 209") { return "side-console"; }
      if (fam === "MA 206") { return "jon"; }
      if (fam === "MA 200" || fam === "MA 200A" || fam === "MA 200B") { return "bowrider"; }
      if (fam === "MA 503") { return "pilothouse"; }
    }
    return null;
  }

  function firstThatExists(names) {
    for (var i = 0; i < names.length; i++) {
      if (names[i] && HAS[names[i]]) { return names[i]; }
    }
    return null;
  }

  /* Returns "os_….png" for a finder row, or null if nothing fits. */
  function pick(row) {
    if (!row) { return null; }
    var cls = clauses(row.configLabel);
    var hull = hullOf(row.boatType);
    var layout = layoutFromModel(row.model, row.baseModel)
      || layoutFromType(row.boatType)
      || layoutFromCovers(row.covers)
      || "side-console";

    var tm = feature(cls, /TROLLING MOTOR|THRUSTER/) === true;
    var jack = feature(cls, /JACK PLATE|SET BACK PLATE/) === true;
    var ski = feature(cls, /SKI TOW|SKI PYLON/) === true;
    var arch = feature(cls, /WATERSPORTS ARCH|SPORTS ARCH|SKI TOW ARCH/) === true;
    var bimini = feature(cls, /BIMINI (TOP )?(LAID BACK|FOLDED)|BIMINI FOLDED/) === true;
    var swim = feature(cls, /SWIM PLATFORM/) === true;
    var sterndrive = feature(cls, /STERNDRIVE|INBOARD/) === true;
    var twin = feature(cls, /TWIN ENGINE/) === true;
    var rails = feature(cls, /BOW RAIL|SIDE RAIL|AFT RAIL/) === true;
    var windshield = feature(cls, /FULL WINDSHIELD/) === true;

    var hardTop = feature(cls, /HARD T-TOP|HARD OR SOFT T-TOP|\bT-?\s?TOP\b/);
    var softOnly = feature(cls, /SOFT T-TOP/) === true && feature(cls, /HARD T-TOP|HARD OR SOFT T-TOP/) !== true;
    var top = hardTop === true ? (softOnly ? "soft-ttop" : "hard-ttop")
      : hardTop === false ? "no-ttop"
        : "";

    var eng = twin ? "twin-engine" : "single-engine";
    var name;

    switch (layout) {
      case "center-console": {
        var extra = windshield ? "full-windshield" : rails ? "rails" : ski ? "ski-tow" : "";
        name = firstThatExists([
          top && extra ? "os_center-console_" + top + "_" + eng + "_" + extra : null,
          top ? "os_center-console_" + top + "_" + eng : null,
          top ? "os_center-console_" + top + "_single-engine" : null,
          extra ? "os_center-console_" + eng + "_" + extra : null,
          extra ? "os_center-console_single-engine_" + extra : null,
          "os_center-console_" + eng,
          "os_center-console_single-engine"
        ]);
        break;
      }
      case "side-console": {
        if (hull === "bass") {
          name = firstThatExists([
            tm && jack ? "os_bass-side-console_trolling-motor_jack-plate" : null,
            tm ? "os_bass-side-console_trolling-motor" : null,
            jack ? "os_bass-side-console_jack-plate" : null,
            "os_bass-side-console"
          ]);
        } else if (hull === "deep-v") {
          name = firstThatExists([
            tm ? "os_deep-v-side-console_trolling-motor" : null,
            "os_deep-v-side-console"
          ]);
        } else if (hull === "mod-v") {
          name = "os_mod-v-side-console";
        } else if (hull === "aluminum") {
          name = "os_aluminum-side-console";
        } else {
          name = firstThatExists([
            arch ? "os_side-console_sports-arch" : null,
            bimini ? "os_side-console_bimini-folded" : null,
            ski ? "os_side-console_ski-tow" : null,
            tm && jack ? "os_side-console_trolling-motor_jack-plate" : null,
            tm ? "os_side-console_trolling-motor" : null,
            "os_side-console"
          ]);
        }
        break;
      }
      case "dual-console": {
        if (hull === "bass") {
          name = firstThatExists([
            tm && jack ? "os_bass-dual-console_trolling-motor_jack-plate" : null,
            tm ? "os_bass-dual-console_trolling-motor" : null,
            "os_bass-dual-console"
          ]);
        } else if (hull === "deep-v") {
          name = "os_deep-v-dual-console";
        } else {
          name = "os_dual-console_single-engine";
        }
        break;
      }
      case "tiller": {
        if (hull === "aluminum") {
          name = firstThatExists([tm ? "os_aluminum-tiller_trolling-motor" : null, "os_aluminum-tiller"]);
        } else if (hull === "mod-v") {
          name = firstThatExists([tm ? "os_mod-v-tiller_trolling-motor" : null, "os_mod-v-tiller"]);
        } else if (hull === "deep-v") {
          name = firstThatExists([tm ? "os_deep-v-tiller_trolling-motor" : null, "os_deep-v-tiller"]);
        } else if (hull === "bass") {
          name = firstThatExists([
            tm && jack ? "os_bass-side-console_trolling-motor_jack-plate" : null,
            tm ? "os_bass-side-console_trolling-motor" : null,
            "os_bass-side-console"
          ]);
        } else {
          name = firstThatExists([jack ? "os_tiller_jack-plate" : null, "os_tiller"]);
        }
        break;
      }
      case "jon": {
        name = /\bTILLER\b/.test(norm(row.model + " " + row.baseModel)) ? "os_jon-tiller" : "os_jon-open";
        break;
      }
      case "bowrider": {
        var drive = sterndrive ? "sterndrive" : "single-engine";
        name = firstThatExists([
          bimini && swim ? "os_bowrider_" + drive + "_bimini-folded_swim-platform" : null,
          swim ? "os_bowrider_" + drive + "_swim-platform" : null,
          bimini ? "os_bowrider_" + drive + "_bimini-folded_swim-platform" : null,
          "os_bowrider_" + drive
        ]);
        break;
      }
      case "walkaround": {
        name = (twin || top === "hard-ttop") ? "os_walkaround_hard-ttop_twin-engine" : "os_walkaround_single-engine";
        break;
      }
      case "pilothouse": {
        name = "os_pilothouse_hard-ttop_twin-engine";
        break;
      }
      default:
        name = null;
    }

    return name && HAS[name] ? name + ".png" : null;
  }

  pick.files = FILES.slice();
  return pick;
});
